import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Artist,
  ArtistsData,
  ArtistsQueryParams,
  CreateArtistRequest,
  UpdateArtistRequest,
} from '../../types/api/artist';

export class ArtistService {
  private httpClient = HttpClient.getInstance();

  async getArtists(params: ArtistsQueryParams = {}): Promise<ArtistsData> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.append('page', String(params.page));
    if (params.size !== undefined) query.append('size', String(params.size));
    if (params.keyword) query.append('keyword', params.keyword);
    const url = `${API_ENDPOINTS.ARTISTS.ADMIN_LIST}?${query}`;
    const response = await this.httpClient.get<ArtistsData>(url);
    if (!response.success) throw new Error(response.message || 'Failed to fetch artists');
    return response.data;
  }

  async getArtist(artistId: string): Promise<Artist> {
    const response = await this.httpClient.get<Artist>(API_ENDPOINTS.ARTISTS.ADMIN_DETAIL(artistId));
    if (!response.success) throw new Error(response.message || 'Failed to fetch artist');
    return response.data;
  }

  /**
   * Create an artist. Sends multipart/form-data: `data` (JSON) + optional `image` file.
   */
  async createArtist(artistData: CreateArtistRequest, imageFile?: File): Promise<Artist> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(artistData)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<Artist>({
      method: 'POST',
      url: API_ENDPOINTS.ARTISTS.ADMIN_CREATE,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to create artist');
    return response.data;
  }

  /**
   * Update an artist. Sends multipart/form-data: `data` (JSON) + optional replacement `image` file.
   */
  async updateArtist(artistId: string, artistData: UpdateArtistRequest, imageFile?: File): Promise<Artist> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(artistData)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<Artist>({
      method: 'PUT',
      url: API_ENDPOINTS.ARTISTS.ADMIN_UPDATE(artistId),
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to update artist');
    return response.data;
  }

  async deleteArtist(artistId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.ARTISTS.ADMIN_DELETE(artistId)
    );
    if (!response.success) throw new Error(response.message || 'Failed to delete artist');
  }
}
